import {
  Avatar,
  Box,
  HStack,
  Icon,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiArrowLeftLine } from "@remixicon/react";
import Pusher from "pusher-js";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  ActiveChatInterface,
  ChatInterface,
  ProductChatInterface,
  ProductParamInterface,
} from "../../constants/Chat";
import useCustomToast from "../../lib/useCustomToast";
import { useGetUserLoginStore } from "../../store/useGetUserLoginStore";
import { useBgBaseColor, useDarkWhiteColor } from "../../themes/colors";
import { axiosBuyer } from "../../utils/AxiosInstance";
import { getAxiosInstance, isUuid } from "../../utils/helperFunction";
import { ProductChat } from "../dashboard_buyer_page/ProductChat";
import { ChatBox } from "./ChatBox";
import { InputMessage } from "./InputMessage";

interface Props extends StackProps {
  storePrefix?: string | null;
  slug?: string | null;
  selectedChat: string | number | undefined;
  productParam?: ProductParamInterface | undefined;
  handleCloseChat: (action: boolean) => void;
  onSendMessage: (id_chat: string) => void;
  setSelectedChat: Dispatch<SetStateAction<string | number | undefined>>;
  clientRole?: string;
}

export const ActiveChat = ({
  storePrefix = "",
  slug = "",
  selectedChat,
  productParam,
  setSelectedChat,
  handleCloseChat,
  onSendMessage,
  clientRole,
  ...rest
}: Props) => {
  const [data, setData] = useState<ActiveChatInterface | undefined>(undefined);
  const [messages, setMessages] = useState<ChatInterface[]>([]);
  const [productChat, setProductChat] = useState<
    ProductChatInterface | undefined
  >(undefined);
  const { currentActiveUser } = useGetUserLoginStore();
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const toast = useCustomToast();
  const bgBnw = useDarkWhiteColor();
  const bgBase = useBgBaseColor();

  useEffect(() => {
    const pusher = new Pusher(`${process.env.REACT_APP_PUSHER_APP_KEY}`, {
      cluster: `${process.env.REACT_APP_PUSHER_APP_CLUSTER}`,
    });

    // Subscribe to the correct chat channel
    let channel = undefined;

    isUuid(data?.id)
      ? (channel = pusher.subscribe(`chat.${data?.id}`))
      : (channel = undefined);

    // Listen for new messages
    if (channel) {
      channel.bind("MessageSent", (dataMsg: ChatInterface) => {
        setMessages((prev) => {
          const updatedMessages = [...prev, dataMsg];
          return updatedMessages;
        });
      });
    }

    // Cleanup: Unsubscribe when the component is unmounted
    return () => {
      pusher.unsubscribe(`chat.${data?.id}`);
    };
  }, [currentActiveUser?.id, data, selectedChat]);

  useEffect(() => {
    let pageType = 1; //buyer
    if (currentActiveUser && selectedChat) {
      if (currentActiveUser?.role === "supplier") {
        pageType = 3;
      }

      const axiosInstance = getAxiosInstance(pageType);
      axiosInstance
        .get(
          `/v1/chat/chats/${selectedChat}/${currentActiveUser?.id}/${currentActiveUser?.role}`
        )
        .then((res) => {
          setData(res.data.data);
          setMessages(res.data.data.chat);
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {});
    }
  }, [productParam, selectedChat, currentActiveUser]);

  useEffect(() => {
    if (productParam) {
      const urlApi = `/v1/buyer/product-chat?supplierId=${productParam.supplierId}&productId=${productParam.productId}&variantId=${productParam.variantId}&storePrefix=${storePrefix}&slug=${slug}`;
      axiosBuyer
        .get(urlApi)
        .then((res) => {
          setProductChat(res.data.data);
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {});
    }
  }, []);

  useEffect(() => {
    isUuid(data?.id) && setSelectedChat(data?.id);
  }, [data?.id, setSelectedChat]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [data, messages]);

  const clearSelectedChat = () => {
    handleCloseChat(true);
  };

  return (
    <VStack
      className="right-chat-layout"
      w={{ base: "100%", md: "70%" }}
      gap={0}
      position={"relative"}
      overflowY={"auto"}
      {...rest}
    >
      <HStack
        className="header-profile-supplier"
        w={"100%"}
        px={[4, null, 5]}
        py={[4, null, 3]}
        bg={bgBnw}
        position={{ base: "fixed", md: "sticky" }}
        top={{ base: "60px", md: "0" }}
        zIndex={10}
      >
        <Icon
          as={RiArrowLeftLine}
          fontSize={[25, null, 28]}
          cursor={"pointer"}
          onClick={clearSelectedChat}
        />
        <Avatar
          name={data?.name || ""}
          src={
            data?.avatar
              ? `${process.env.REACT_APP_MEDIA_URL}${data?.avatar}`
              : ""
          }
          size={["xs", null, "sm"]}
        />

        <Text textStyle={"h-reg-title"} fontSize="md">
          {data?.name}
        </Text>
        <Text fontSize="md" color="gray.500">
          {clientRole === "supplier" ? "Supplier" : "Pembeli"}
        </Text>
      </HStack>

      <VStack
        className="chat-box"
        bg={bgBase}
        h={"100%"}
        w={"100%"}
        align={"stretch"}
        overflowY={"auto"}
        paddingTop={{ base: 16, md: 2 }}
        paddingBottom={{ base: 16, md: 2 }}
      >
        {messages &&
          messages.map((item, i) => (
            <>
              {i === 0 ||
              new Date(item.timestamp).getDate() !==
                new Date(messages[i - 1].timestamp).getDate() ? (
                <Text
                  key={i}
                  fontSize="sm"
                  color="gray.500"
                  alignSelf="center"
                  paddingY={2}
                >
                  {getDateTime(item.timestamp)}
                </Text>
              ) : null}
              <ChatBox key={i} data={item} />
            </>
          ))}

        <Box ref={chatEndRef} />
      </VStack>

      {productParam && productChat && (
        <ProductChat
          data={productChat}
          onCloseProduct={() => {
            setProductChat(undefined);
          }}
        />
      )}

      <InputMessage
        userId={data?.user_id}
        selectedChat={selectedChat}
        productChat={productChat}
        productParam={productParam}
        onSendMessage={(id_chat) => onSendMessage(id_chat)}
      />
    </VStack>
  );
};

const getDateTime = (date: string | Date) => {
  const dateObj = new Date(date);
  if (new Date().getDate() - dateObj.getDate() < 1) {
    return "Hari ini";
  } else if (new Date().getDate() - dateObj.getDate() === 1) {
    return "Kemarin";
  } else if (new Date().getDate() - dateObj.getDate() < 7) {
    return dateObj.toLocaleDateString("id-ID", { weekday: "long" });
  }
  return dateObj.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};
